import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { callSubmitWidget } from '../services/submissionService';

const TAG_OPTIONS = [
  'analysis', 'play', 'learn', 'openings', 'people',
  'books', 'news'
];

const SOCIAL_PLATFORMS = [
  { key: 'twitter', label: 'Twitter / X', placeholder: 'x.com/username' },
  { key: 'youtube', label: 'YouTube', placeholder: 'youtube.com/@channel' },
  { key: 'instagram', label: 'Instagram', placeholder: 'instagram.com/username' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'tiktok.com/@username' },
  { key: 'twitch', label: 'Twitch', placeholder: 'twitch.tv/username' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/username' },
  { key: 'github', label: 'GitHub', placeholder: 'github.com/username' },
  { key: 'discord', label: 'Discord', placeholder: 'discord.gg/invite' },
  { key: 'website', label: 'Website', placeholder: 'yoursite.com' },
];

const SubmitListingForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    subtitle: '',
    description: '',
    tag: '',
    submitterName: '',
    submitterEmail: '',
  });
  const [links, setLinks] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'subtitle') {
      const lower = value.toLowerCase();
      // Enforce max 3 words for short description
      const words = lower.trim().split(/\s+/).filter(Boolean);
      if (words.length > 3 && lower.endsWith(' ')) return;
      if (words.length > 3) {
        setFormData(prev => ({ ...prev, [name]: words.slice(0, 3).join(' ') }));
        return;
      }
      setFormData(prev => ({ ...prev, [name]: lower }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagSelect = (tag) => {
    setFormData(prev => ({
      ...prev,
      tag: prev.tag === tag ? '' : tag,
    }));
    // Clear social links when deselecting the current tag
    if (tag === formData.tag) {
      setLinks({});
    }
  };

  const handleLinkChange = (platform, value) => {
    setLinks(prev => {
      const updated = { ...prev };
      if (value.trim()) {
        updated[platform] = value;
      } else {
        delete updated[platform];
      }
      return updated;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setStatus('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setStatus('Image must be smaller than 5MB');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setStatus('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setStatus('An image/logo is required');
      return;
    }

    setLoading(true);
    setStatus('Uploading image...');

    try {
      // Upload image to Firebase Storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${imageFile.name}`;
      const imageRef = ref(storage, `widget-images/${fileName}`);
      const snapshot = await uploadBytes(imageRef, imageFile);
      const iconUrl = await getDownloadURL(snapshot.ref);

      setStatus('Submitting listing...');

      // Normalize URL — prepend https:// if no protocol
      let normalizedLink = formData.link.trim();
      if (normalizedLink && !/^https?:\/\//i.test(normalizedLink)) {
        normalizedLink = `https://${normalizedLink}`;
      }

      // Normalize social links — prepend https:// if needed
      const normalizedLinks = {};
      for (const [platform, url] of Object.entries(links)) {
        let u = url.trim();
        if (u && !/^https?:\/\//i.test(u)) u = `https://${u}`;
        if (u) normalizedLinks[platform] = u;
      }

      // Call the Cloud Function
      await callSubmitWidget({
        title: formData.title.trim(),
        link: normalizedLink,
        subtitle: formData.subtitle.trim(),
        description: formData.description.trim(),
        tag: formData.tag || '',
        icon: iconUrl,
        links: Object.keys(normalizedLinks).length > 0 ? normalizedLinks : {},
        source: 'public_form',
        submitterName: formData.submitterName.trim() || null,
        submitterEmail: formData.submitterEmail.trim() || null,
      });

      setStatus('Listing submitted for review! We\'ll review it shortly.');
      setFormData({
        title: '', link: '', subtitle: '', description: '',
        tag: '', submitterName: '', submitterEmail: '',
      });
      setLinks({});
      setImageFile(null);
      setImagePreview(null);
    } catch (error) {
      console.error('Submission error:', error);
      if (error.code === 'functions/resource-exhausted') {
        setStatus('Too many submissions. Please try again later.');
      } else {
        setStatus('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="text-2xl font-semibold text-gray-100 mb-2 text-center"
      >
        Submit a Listing
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="text-gray-400 text-sm mb-8 text-center"
      >
        Know a chess app, tool, or person that belongs on TrendingChess? Submit it below and we'll review it.
      </motion.p>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {/* Title */}
        <div>
          <label className="block text-sm text-gray-300 mb-1">Name *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            maxLength={200}
            placeholder="e.g. ChessTrainer Pro"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
          />
        </div>

        {/* Link */}
        <div>
          <label className="block text-sm text-gray-300 mb-1">URL *</label>
          <input
            type="text"
            name="link"
            value={formData.link}
            onChange={handleChange}
            required
            maxLength={500}
            placeholder="example.com"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
          />
          <p className="text-xs text-gray-500 mt-1">e.g. google.com or https://google.com</p>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm text-gray-300 mb-1">Logo / Image *</label>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className="px-4 py-3 bg-gray-800 border border-gray-700 border-dashed rounded-lg text-gray-400 text-center hover:border-gray-500 transition-colors">
                {imageFile ? imageFile.name : 'Click to upload image (max 5MB)'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="w-12 h-12 rounded-lg object-cover border border-gray-700"
              />
            )}
          </div>
        </div>

        {/* Subtitle — max 3 words, displayed on widget cards */}
        <div>
          <label className="block text-sm text-gray-300 mb-1">Short Description (max 3 words) *</label>
          <input
            type="text"
            name="subtitle"
            value={formData.subtitle}
            onChange={handleChange}
            required
            maxLength={60}
            placeholder="e.g. Free Chess Training"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>This appears on your listing card</span>
            <span>{formData.subtitle.trim().split(/\s+/).filter(Boolean).length}/3 words</span>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm text-gray-300 mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            maxLength={500}
            rows={4}
            placeholder="What does this app, tool, or creator do? Key features, what makes it unique, who it's for..."
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors resize-none"
          />
          <div className="flex justify-end text-xs text-gray-500 mt-1">
            <span>{formData.description.length}/500</span>
          </div>
        </div>

        {/* Tag — single select */}
        <div>
          <label className="block text-sm text-gray-300 mb-2">Category</label>
          <div className="flex flex-wrap gap-2">
            {TAG_OPTIONS.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagSelect(tag)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  formData.tag === tag
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-500'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Social Media Links — always visible */}
        <div className="space-y-3">
          <label className="block text-sm text-gray-300">Social Media Links</label>
          {SOCIAL_PLATFORMS.map(({ key, label, placeholder }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-20 shrink-0">{label}</span>
              <input
                type="text"
                value={links[key] || ''}
                onChange={(e) => handleLinkChange(key, e.target.value)}
                maxLength={500}
                placeholder={placeholder}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors text-sm"
              />
            </div>
          ))}
        </div>

        {/* Submitter Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Your Name (optional)</label>
            <input
              type="text"
              name="submitterName"
              value={formData.submitterName}
              onChange={handleChange}
              maxLength={100}
              placeholder="Your name"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
              autoComplete="name"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Your Email (optional)</label>
            <input
              type="email"
              name="submitterEmail"
              value={formData.submitterEmail}
              onChange={handleChange}
              maxLength={200}
              placeholder="you@example.com"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
              autoComplete="email"
            />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading || !formData.title.trim() || !formData.link.trim() || !formData.subtitle.trim() || !imageFile}
          className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200"
        >
          {loading ? 'Submitting...' : 'Submit for Review'}
        </button>

        {status && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`text-center mt-4 ${
              status.includes('submitted') || status.includes('review') ? 'text-green-400' :
              status.includes('error') || status.includes('wrong') || status.includes('many') ? 'text-red-400' :
              'text-gray-400'
            }`}
            role="status"
            aria-live="polite"
          >
            {status}
            {(status.includes('submitted') || status.includes('review')) && (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-4 block mx-auto px-6 py-2 bg-gray-800 border border-gray-700 hover:border-gray-500 text-gray-300 rounded-lg transition-colors"
              >
                ← Return to Trending Chess
              </button>
            )}
          </motion.div>
        )}
      </motion.form>
    </div>
  );
};

export default SubmitListingForm;
